import { OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { orgRootPath } from '../organization/useOrg'
import { usePreferredPayrollView } from '../payroll/usePreferredPayrollView'
import { HealthcheckStatusResult, HealthcheckType } from '../types'
import { useMultiPayGroupsFormStatus } from '../workLocations/LocationsSurvey'

/**
 * Hook that returns a function to determine navigation path for healthcheck items
 */
export const useGetNavigationPath = (org: OrganizationDetailFragment) => {
  const preferredPayrollView = usePreferredPayrollView(org)
  const formStatus = useMultiPayGroupsFormStatus(org)
  const currentOrgRootPath = orgRootPath(org.id)

  return (
    healthcheckType: HealthcheckType,
    { status }: Pick<HealthcheckStatusResult, 'status'>
  ): `/${string}` | string | null => {
    switch (healthcheckType) {
      case OnboardingStepType.OrderForm:
        return status === 'complete' ? `${currentOrgRootPath}/documents` : `${currentOrgRootPath}/order-form`
      case OnboardingStepType.CompanyProfile:
        return `${currentOrgRootPath}/company-profile`
      case OnboardingStepType.CompleteKyc:
        return status === 'complete' ? null : `${currentOrgRootPath}/company-representative`
      case OnboardingStepType.ConnectPayroll:
        if (preferredPayrollView === 'read-access') {
          return `${currentOrgRootPath}/payroll/read-access`
        } else if (preferredPayrollView === 'write-access') {
          return `${currentOrgRootPath}/payroll/write-access`
        } else if (preferredPayrollView === 'survey') {
          return `${currentOrgRootPath}/payroll/survey`
        }
        return `${currentOrgRootPath}/payroll/write-access`
      case OnboardingStepType.WorkLocations: {
        const subpath = formStatus !== 'complete' ? 'multi-pay-groups' : undefined
        return `${currentOrgRootPath}/locations${subpath ? `/${subpath}` : ''}`
      }
      case OnboardingStepType.ConnectShifts: {
        return `${currentOrgRootPath}/shift-scheduling`
      }
      case OnboardingStepType.ConnectBilling:
        return `${currentOrgRootPath}/billing`
      case OnboardingStepType.ConnectBanking: {
        const subpath2 = status === 'incomplete' ? '/account/setup' : ''
        return `${currentOrgRootPath}/alice-card-program${subpath2}`
      }
      case 'enrollmentSuccess':
        return `${currentOrgRootPath}/enrollment-success`
      case OnboardingStepType.SetLaunchDate:
        return currentOrgRootPath // No navigation for launch date
      default:
        return currentOrgRootPath
    }
  }
}
